import {dark, light} from '@/shared/ui/theme';
import type {ThemeMode} from '@/shared/ui/theme-mode';

/**
 * Put the browser's own furniture in the theme the app has chosen.
 *
 * Four things outside React's tree are painted by the user agent, not by us,
 * and all four read the document rather than the component tree:
 *
 *   - the Android URL bar and the task-switcher card → `<meta name="theme-color">`
 *   - the page behind the app's own background (the bounce area, and every
 *     frame before React commits) → the `<html>` background colour
 *   - scrollbars, form controls, autofill → `color-scheme`
 *   - a print, a screenshot, a "reader" overlay → the same two above
 *
 * `web/index.html` declares all of this for the SYSTEM scheme, with a media
 * query, which is exactly right until the user picks a mode that disagrees with
 * the system. From then on the media query is a second opinion that must lose:
 * the meta tags it wrote are removed and replaced with a single unconditional
 * one, so a light app inside a dark OS gets a light URL bar rather than a dark
 * strip above a white page.
 *
 * Choosing `system` again drops the override and hands the media queries back.
 */
const MANAGED = 'sv-theme-color';

/**
 * Matches the `theme-color` pair in web/index.html — keep the two in step.
 *
 * Обе — цвет ОПРАВЫ (`chrome`): она следует за темой, и адресная строка
 * продолжает верхнюю полосу кабинета — светлую на свету, тёмно-серую в темноте.
 */
const THEME_COLOR = {light: light.chrome, dark: dark.chrome} as const;

function metaFor(scheme: 'light' | 'dark'): HTMLMetaElement {
  let tag = document.querySelector<HTMLMetaElement>(`meta[data-${MANAGED}]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'theme-color';
    tag.setAttribute(`data-${MANAGED}`, '');
    document.head.appendChild(tag);
  }
  tag.content = THEME_COLOR[scheme];
  return tag;
}

/**
 * `true` while a mode other than `system` is in force. The media-query metas in
 * index.html are disabled for exactly that long: `media="not all"` neutralises
 * a `<meta>` without removing it, so switching back to `system` is one flag
 * flip rather than a re-parse of the head.
 */
function setStaticMetasEnabled(enabled: boolean): void {
  const tags = document.querySelectorAll<HTMLMetaElement>(
    'meta[name="theme-color"][media]',
  );
  tags.forEach(tag => {
    const original = tag.getAttribute('data-sv-media') ?? tag.media;
    if (!tag.hasAttribute('data-sv-media')) {
      tag.setAttribute('data-sv-media', original);
    }
    tag.media = enabled ? original : 'not all';
  });
}

export function applyDocumentTheme(
  scheme: 'light' | 'dark',
  mode: ThemeMode = 'system',
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const palette = scheme === 'dark' ? dark : light;
  const root = document.documentElement;

  root.style.backgroundColor = palette.background;
  // The boot splash in web/index.html is drawn from these three, before React
  // exists. Keeping them current means a reload never repaints the splash in
  // the colour the app just moved away from.
  root.style.setProperty('--sv-bg', palette.background);
  root.style.setProperty('--sv-fg', palette.foreground);
  root.style.setProperty('--sv-brand', palette.primary);
  // The scrollbar, which index.html otherwise picks by media query — an override
  // has to carry it too, or a light app inside a dark OS gets pale scrollbars on white.
  root.style.setProperty('--sv-scroll', palette.borderStrong);
  root.style.setProperty('--sv-scroll-hover', palette.mutedForeground);
  root.style.setProperty('--sv-scroll-track', palette.inset);
  // Read by the standalone-mode rules in web/index.html, and by anyone
  // debugging why the page is the colour it is.
  root.dataset.theme = scheme;

  if (document.body) {
    document.body.style.backgroundColor = palette.background;
  }

  if (mode === 'system') {
    setStaticMetasEnabled(true);
    document.querySelector(`meta[data-${MANAGED}]`)?.remove();
    // Hand the splash tokens back to the media query too: an inline custom
    // property outranks it, so leaving them set would pin the splash to
    // whatever was chosen before `system` was chosen again.
    root.style.removeProperty('--sv-bg');
    root.style.removeProperty('--sv-fg');
    root.style.removeProperty('--sv-brand');
    root.style.removeProperty('--sv-scroll');
    root.style.removeProperty('--sv-scroll-hover');
    root.style.removeProperty('--sv-scroll-track');
    // `color-scheme: light dark` is what index.html declares; restoring the
    // pair lets the media queries own the answer again.
    root.style.colorScheme = 'light dark';
    return;
  }

  setStaticMetasEnabled(false);
  root.style.colorScheme = scheme;
  metaFor(scheme);
}
